import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { z } from "zod";

import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";
import { useTRPC } from "@/utils/trpc";

const acceptInviteSearchSchema = z.object({
  token: z.string().catch(""),
});

export const Route = createFileRoute("/(app)/accept-invite")({
  validateSearch: acceptInviteSearchSchema,
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCurrentWorkspaceId } = useCurrentWorkspace();
  const viewer = useQuery(trpc.viewer.me.queryOptions());

  const invite = useQuery({
    ...trpc.workspaces.members.getInvite.queryOptions({ token }),
    enabled: token.length > 0,
  });

  const accept = useMutation(
    trpc.workspaces.members.acceptInvite.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async (result) => {
        toast.success("Invitation accepted");
        track("invite_accepted");
        // Land the user *in* the workspace they just joined. Without this they
        // are dropped back into whichever workspace was already selected, and
        // nothing on screen changes — the accept looks like it did nothing.
        setCurrentWorkspaceId(result.workspaceId);
        // The sidebar/switcher reads `viewer.bootstrap`, not `workspaces.list`
        // — invalidating only the latter left the new workspace invisible
        // until a full page reload.
        await queryClient.invalidateQueries(
          trpc.viewer.bootstrap.queryFilter(),
        );
        await navigate({ to: "/queue" });
      },
    }),
  );

  // An invite is bound to the address it was sent to, so a signed-in user on a
  // different account can't accept it. Say so up front rather than letting them
  // click through to a server error.
  const viewerEmail = viewer.data?.user.email?.trim().toLowerCase() ?? null;
  const inviteEmail = invite.data?.email.trim().toLowerCase() ?? null;
  const isWrongAccount =
    viewerEmail !== null && inviteEmail !== null && viewerEmail !== inviteEmail;

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md items-center">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Join a workspace</CardTitle>
          <CardDescription>
            You've been invited to collaborate on PostDaily.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <Alert variant="destructive">
              <AlertTitle>Missing invitation</AlertTitle>
              <AlertDescription>
                This link is missing its invitation token.
              </AlertDescription>
            </Alert>
          ) : invite.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : invite.isError || !invite.data ? (
            <Alert variant="destructive">
              <AlertTitle>Invitation unavailable</AlertTitle>
              <AlertDescription>
                This invitation is invalid, already used, or has expired. Ask a
                workspace admin to send a new one.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 rounded-lg border p-4">
                <p className="font-medium text-sm">
                  {invite.data.workspaceName}
                </p>
                <p className="text-muted-foreground text-sm">
                  {invite.data.invitedByName
                    ? `${invite.data.invitedByName} invited you`
                    : "You were invited"}{" "}
                  as {invite.data.role === "admin" ? "an" : "a"}{" "}
                  <span className="capitalize">{invite.data.role}</span>.
                </p>
                <p className="text-muted-foreground text-sm">
                  Sent to{" "}
                  <span className="font-medium">{invite.data.email}</span>
                </p>
              </div>
              {isWrongAccount ? (
                <Alert variant="destructive">
                  <AlertTitle>Signed in as a different account</AlertTitle>
                  <AlertDescription>
                    This invitation was sent to {invite.data.email}, but you're
                    signed in as {viewer.data?.user.email}. Sign in with the
                    invited address, or ask for a new invitation.
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Link
            to="/queue"
            className="text-muted-foreground text-sm hover:underline"
          >
            Not now
          </Link>
          <Button
            type="button"
            disabled={
              !token || !invite.data || accept.isPending || isWrongAccount
            }
            onClick={() => accept.mutate({ token })}
          >
            {accept.isPending ? <Spinner data-icon="inline-start" /> : null}
            Accept invitation
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
